import type { SheetName, ParsedSheets } from './xlsxParser';

/**
 * Given a set of present sheet names, returns them in topologically sorted
 * execution order based on cross-entity dependencies.
 *
 * Pass 0 (no deps): location_types, organization_types, topic_types, service_types
 * Pass 1 (depends on types): locations (root), organizations (root), topics (root),
 *                             sections (root), service_definitions
 * Pass 2 (self-ref children): locations with parent, organizations with parent,
 *                              topics with parent, sections with parent
 * Pass 3 (depends on locations): users
 */

const EXECUTION_ORDER: SheetName[] = [
  // Pass 0 — independent types
  'location_types',
  'organization_types',
  'topic_types',
  'service_types',
  // Pass 1 — primary entities (root only, then with parents)
  'locations',
  'organizations',
  'topics',
  'sections',
  'service_definitions',
  // Pass 2 — entities depending on locations
  'users',
];

export function resolveExecutionOrder(sheets: ParsedSheets): SheetName[] {
  return EXECUTION_ORDER.filter((name) => name in sheets && (sheets[name]?.length ?? 0) > 0);
}

/**
 * Split rows for a hierarchical entity into two passes:
 * 1. Roots (no parent reference)
 * 2. Children (has parent reference)
 */
export function splitHierarchical<T extends Record<string, string>>(
  rows: T[],
  parentField: string
): { roots: T[]; children: T[] } {
  const roots: T[] = [];
  const children: T[] = [];
  for (const row of rows) {
    if (row[parentField]) {
      children.push(row);
    } else {
      roots.push(row);
    }
  }
  return { roots, children };
}



